import React, { useEffect, useRef, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Box, Text, Select, VStack, Spinner, useToast, Button, HStack
} from '@chakra-ui/react';

const SCANNER_ELEMENT_ID = 'taga-barcode-scanner-region';

const BarcodeScanner = ({ isOpen, onClose, onDetected }) => {
  const toast = useToast();
  const html5QrCodeRef = useRef(null);
  const lastReadRef = useRef({ code: '', at: 0 });
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setError(null);

    const startScanner = async () => {
      try {
        setStarting(true);
        const { Html5Qrcode } = await import('html5-qrcode');

        if (cancelled) return;

        const devices = await Html5Qrcode.getCameras();
        if (cancelled) return;

        if (!devices || devices.length === 0) {
          setError('Nenhuma câmera disponível neste dispositivo.');
          setStarting(false);
          return;
        }

        setCameras(devices);

        const traseira = devices.find(d => /back|rear|trase/i.test(d.label));
        const selectedId = traseira?.id || devices[devices.length - 1].id;
        setCameraId(selectedId);

        html5QrCodeRef.current = new Html5Qrcode(SCANNER_ELEMENT_ID, { verbose: false });

        await html5QrCodeRef.current.start(
          selectedId,
          {
            fps: 10,
            qrbox: (vw, vh) => {
              const min = Math.min(vw, vh);
              const size = Math.floor(min * 0.7);
              return { width: size, height: Math.floor(size * 0.6) };
            },
            aspectRatio: 1.7777,
          },
          (decodedText) => {
            const now = Date.now();
            if (lastReadRef.current.code === decodedText && (now - lastReadRef.current.at) < 1500) {
              return;
            }
            lastReadRef.current = { code: decodedText, at: now };
            onDetected?.(decodedText);
          },
          () => {}
        );

        setStarting(false);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.message || String(err);
        setError(msg.includes('Permission') || msg.includes('NotAllowed')
          ? 'Permissão de câmera negada. Habilite no navegador.'
          : 'Falha ao iniciar câmera: ' + msg);
        setStarting(false);
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      const inst = html5QrCodeRef.current;
      html5QrCodeRef.current = null;
      if (inst) {
        try {
          if (inst.isScanning) {
            inst.stop().then(() => inst.clear()).catch(() => {});
          } else {
            inst.clear();
          }
        } catch {}
      }
    };
  }, [isOpen, onDetected]);

  const trocarCamera = async (novoId) => {
    const inst = html5QrCodeRef.current;
    if (!inst) return;
    try {
      setStarting(true);
      if (inst.isScanning) {
        await inst.stop();
      }
      await inst.start(
        novoId,
        {
          fps: 10,
          qrbox: (vw, vh) => {
            const min = Math.min(vw, vh);
            const size = Math.floor(min * 0.7);
            return { width: size, height: Math.floor(size * 0.6) };
          },
          aspectRatio: 1.7777,
        },
        (decodedText) => {
          const now = Date.now();
          if (lastReadRef.current.code === decodedText && (now - lastReadRef.current.at) < 1500) {
            return;
          }
          lastReadRef.current = { code: decodedText, at: now };
          onDetected?.(decodedText);
        },
        () => {}
      );
      setCameraId(novoId);
    } catch (err) {
      toast({ title: 'Falha ao trocar câmera', description: err?.message, status: 'error' });
    } finally {
      setStarting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Escanear código de barras</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack spacing={3} align="stretch">
            {error ? (
              <Box bg="red.50" border="1px solid" borderColor="red.200" p={3} borderRadius="md">
                <Text color="red.700" fontSize="sm">{error}</Text>
              </Box>
            ) : (
              <Box position="relative" bg="black" borderRadius="md" overflow="hidden">
                <Box id={SCANNER_ELEMENT_ID} w="100%" minH="280px" />
                {starting && (
                  <HStack position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" color="white">
                    <Spinner color="white" />
                    <Text>Iniciando câmera...</Text>
                  </HStack>
                )}
              </Box>
            )}

            {cameras.length > 1 && (
              <Select
                size="sm"
                value={cameraId || ''}
                onChange={(e) => trocarCamera(e.target.value)}
                isDisabled={starting}
              >
                {cameras.map(c => (
                  <option key={c.id} value={c.id}>{c.label || `Câmera ${c.id.slice(-4)}`}</option>
                ))}
              </Select>
            )}

            <Text fontSize="xs" color="gray.500" textAlign="center">
              Aponte para o código de barras. A leitura ocorre automaticamente.
            </Text>

            <Button onClick={onClose} size="sm" variant="outline">Cancelar</Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BarcodeScanner;
